import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PasswordCriteriaHelper } from "@/Common/Helpers/PasswordCriteriaHelper";
import { Error } from "@/Common/Models/Error";
import { LocalizedTextDto } from "@/Common/Models/LocalizedTextDto";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { Button } from "@/DesignSystem";

import styles from "./ChangePasswordPoupControl.module.scss";

interface IChangePasswordForPopupProps {
    onClosePopupCallBack: () => void;
    onSuccess: (newPassword?: string) => void;
    isAdminReset: boolean;
    isAdminResetEmployeeId?: string;
    popupTitle: string;
    stringProvider?: ResourceProvider;
    bypassComplexityValidation?: boolean;
    userHasAdminPermission?: boolean;
    isNewAdminUser?: boolean;
    customPasswordRuleMessage?: LocalizedTextDto;
    customAdminPasswordRuleMessage?: LocalizedTextDto;
    customPasswordValidationRegex?: string
}

class ChangePasswordForPopupControlState {
    public hasOldPasswordWarning: boolean;
    public currentPassword: string;
    public newPassword: string;
    public confirmedNewPassword: string;
    public inProgress: boolean;
    public serverError: string;
    public hasPasswordComplexityError: boolean;
    public validationRegex: RegExp;
}

class ChangePasswordPopupControl extends BaseReactComponent<IChangePasswordForPopupProps, ChangePasswordForPopupControlState> {
    private oldPwdId = "__pwdfcsbx001";
    private newPwdId = "__newpwdinputbox";

    constructor(props: IChangePasswordForPopupProps) {
        super(props);

        const initialState = new ChangePasswordForPopupControlState();
        initialState.validationRegex = new RegExp(props.customPasswordValidationRegex);
        this.state = initialState;
    }

    public render(): JSX.Element {
        const hasPwdMismatchWarning =
            !(IsNullOrWhiteSpace(this.state.newPassword) || IsNullOrWhiteSpace(this.state.confirmedNewPassword))
            && this.state.newPassword !== this.state.confirmedNewPassword;
        const disableChangeButton =
            (this.state.newPassword != this.state.confirmedNewPassword)
            || IsNullOrWhiteSpace(this.state.newPassword)
            || this.state.hasPasswordComplexityError
            || this.state.inProgress;

        const bypassComplexityValidation = this.props.bypassComplexityValidation;

        return (
            <Modal
                title={this.getString(this.props.popupTitle)}
                footer={this.renderFooter(disableChangeButton)}
                width={550}
                showOverlay
                dataCy="login-changePasswordPopupControl"
            >
                <div onKeyDown={e => this.onKeyDown(e, disableChangeButton)}>
                    <div className="p-body">
                        {!bypassComplexityValidation && (
                            <div className="password-requirements">
                                <label>
                                    {PasswordCriteriaHelper.GetPasswordInstruction(
                                        {
                                            customPasswordRuleMessage: this.props.customPasswordRuleMessage,
                                            customAdminPasswordRuleMessage: this.props.customAdminPasswordRuleMessage,
                                            customPasswordValidationRegex: this.props.customPasswordValidationRegex
                                        },
                                        this.props.userHasAdminPermission,
                                        this.props.isNewAdminUser,
                                        this.props.stringProvider
                                    )}
                                </label>
                            </div>
                        )}

                        {!this.props.isAdminReset && (
                            <div className={this.buildErrorCssClass("form-group", this.state.hasOldPasswordWarning)}>
                                <label className="password-labels">{this.getString("OldPasswordLabel")}</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    id={this.oldPwdId}
                                    value={this.state.currentPassword}
                                    onChange={(e: any) => this.handleOldPasswordChanged(e.target.value)}
                                    data-cy="login-oldPassword"
                                />
                                {this.state.hasOldPasswordWarning && <small className="error">{this.getString("OldPasswordMessage")}</small>}
                            </div>
                        )}

                        <div className={this.buildErrorCssClass("form-group", hasPwdMismatchWarning || this.state.hasPasswordComplexityError)}>
                            <label className="password-labels" style={!this.props.isAdminReset ? { marginTop: "10px" } : {}}>{this.getString("PasswordLabel")}</label>
                            <input
                                className="form-control"
                                type="password"
                                value={this.state.newPassword}
                                id={this.newPwdId}
                                onChange={(e: any) => this.handleNewPasswordChanged(e.target.value)}
                                data-cy="login-password"
                            />
                        </div>

                        <div className={this.buildErrorCssClass("form-group", hasPwdMismatchWarning)}>
                            <label className="password-labels" style={{ marginTop: "10px" }}>{this.getString("ConfirmPasswordLabel")}</label>
                            <input
                                className="form-control"
                                type="password"
                                value={this.state.confirmedNewPassword}
                                onChange={(e: any) => this.handleConfirmPasswordChanged(e.target.value)}
                                data-cy="login-confirmPassword"
                            />

                        </div>

                        {hasPwdMismatchWarning &&
                            <div><small className="red-text bold">{this.getString("DifferentPasswordMessage")}</small></div>
                        }
                        {this.state.hasPasswordComplexityError &&
                            <div><small className="red-text bold">{this.getString("PasswordSecurity")}</small> </div>
                        }
                        {!IsNullOrWhiteSpace(this.state.serverError) &&
                            <div><small className="red-text bold">{this.state.serverError}</small></div>
                        }
                    </div>
                </div>
            </Modal>
        );
    }

    private renderFooter(disableChangeButton: boolean): JSX.Element {
        return (
            <div className={styles.footerContainer}>
                <Button
                    variant="neutral"
                    category="secondary"
                    label={this.getString("CancelLabel")}
                    onClick={() => this.props.onClosePopupCallBack()}
                    dataCy="login-cancelButton"
                />
                <Button
                    variant="brand"
                    category="primary"
                    label={this.getString("ChangeLabel")}
                    disabled={disableChangeButton}
                    onClick={() => this.onChangeClick()}
                    dataCy="login-continueButton"
                />
            </div>
        );
    }

    public componentDidMount(): void {
        window.setTimeout(
            () => this.props.isAdminReset
                ? $("#" + this.newPwdId).focus()
                : $("#" + this.oldPwdId).focus(),
            200
        );
    }

    private handleOldPasswordChanged(text: string): void {
        this.mutateState(s => {
            s.currentPassword = text;
            s.hasOldPasswordWarning = false;
            s.serverError = null;
        });
    }

    private handleNewPasswordChanged(text: string): void {
        this.mutateState(s => {
            s.newPassword = text;
            s.hasPasswordComplexityError = !(this.props.bypassComplexityValidation || IsNullOrWhiteSpace(s.newPassword)) && !s.validationRegex.test(s.newPassword);
            s.serverError = null;
        });
    }

    private handleConfirmPasswordChanged(text: string): void {
        this.mutateState(s => {
            s.confirmedNewPassword = text;
            s.serverError = null;
        });
    }

    protected getString(label: string): string {
        // this popup can be mounted from the Login Page, in which case the cacheStore does not exist yet.
        return this.props.stringProvider
            ? this.props.stringProvider.GetString(label, null, sessionStore.locale)
            : cacheStore.GetString(label, null, sessionStore.locale);
    }

    private buildErrorCssClass(baseClass: string, isError: boolean): string {
        return `${baseClass} ${isError ? "red-text bold" : ""}`.trim();
    }

    private onKeyDown(e: React.KeyboardEvent, disableButton: boolean): void {
        if (e.keyCode === 13 && !disableButton)
            this.onChangeClick();
        else if (e.keyCode === 27)
            this.props.onClosePopupCallBack();
    }

    private onChangeClick(): void {
        if (this.state.inProgress)
            return;

        this.mutateState(s => s.inProgress = true);

        if (this.props.isAdminReset) {
            Client.Post(
                ServerOperations.Server.ReinitEmployeePassword,
                {
                    EmployeeId: this.props.isAdminResetEmployeeId,
                    Password: this.state.newPassword,
                    AsTemporary: true
                },
                response => {
                    if (response.Success == true)
                        this.props.onSuccess(this.state.newPassword);
                    else
                        this.mutateStateSRVResponse(s => s.serverError = this.getErrorString(response.Errors));
                },
                () => this.mutateStateSRVResponse()
            );
        }
        else {
            Client.Post(
                ServerOperations.Server.Myle_UserPassword_Change,
                {
                    UserId: sessionStore.userId,
                    Password: this.state.newPassword,
                    OldPassword: this.state.currentPassword
                },
                response => {
                    if (response.Success == true)
                        this.props.onSuccess(this.state.newPassword);
                    else if (response.Errors && response.Errors.length > 0 && response.Errors[0].Code == "BADPWD")
                        this.mutateStateSRVResponse(s => s.hasOldPasswordWarning = true);
                    else
                        this.mutateStateSRVResponse(s => s.serverError = this.getErrorString(response.Errors));
                },
                () => this.mutateStateSRVResponse()
            );
        }
    }

    private getErrorString(error: Error[]): string {
        if (!error?.any())
            return "";

        if (!IsNullOrWhiteSpace(error[0].VocabularyKey))
            return this.getString(error[0].VocabularyKey);

        if (!IsNullOrWhiteSpace(error[0].Code))
            return this.getString(error[0].Code);

        return error[0].Message;
    }

    private mutateStateSRVResponse(mutation: (state: ChangePasswordForPopupControlState) => void = () => null): void {
        this.mutateState(s => {
            s.inProgress = false;
            s.serverError = null;
            mutation(s);
        });
    }
}

export { ChangePasswordPopupControl };
